// =============================================================================
// MENU SCHEMA
// =============================================================================
// This file defines the data model for a restaurant's menu.
// It covers everything from menu categories (Starters, Mains, Desserts)
// to individual items with prices, descriptions, allergens, and dietary tags.
//
// WHY ALLERGENS MATTER:
// - In the EU, Regulation 1169/2011 REQUIRES restaurants to disclose 14 specific
//   allergens for every dish — this is a legal obligation, not optional.
// - In the US, the FALCPA + FASTER Act requires disclosure of 9 major allergens.
// - This schema uses a controlled vocabulary (not free text) so allergen icons
//   and labels are always consistent and legally compliant.
//
// HOW THE MENU IS USED:
// 1. Restaurant owner fills in `content/menu.json` with categories and items
// 2. The MenuSection component renders it as an accessible HTML menu
//    (never as a PDF or image — those are bad for SEO and accessibility)
// 3. Schema.org MenuItem/MenuSection structured data is auto-generated
//    for search engines and AI assistants
// =============================================================================

import { z } from 'zod';

// -----------------------------------------------------------------------------
// Allergen Codes
// -----------------------------------------------------------------------------
// This is the UNION of EU-14 and US-9 allergens, covering both regulatory
// frameworks. The site.config.ts `allergenRegulation` setting determines
// which ones are displayed and which are required vs optional.

/**
 * All possible allergen codes.
 *
 * EU-14 (Regulation 1169/2011): All 14 below
 * US-9 (FALCPA + FASTER Act): milk, eggs, fish, crustaceans, treeNuts,
 *   peanuts, wheat, soybeans, sesame
 *
 * The framework shows icons + text labels for each allergen — never
 * color-only (that would fail WCAG accessibility requirements).
 */
export const AllergenCode = z.enum([
  // --- Present in BOTH EU and US regulations ---
  'milk', // Milk and milk products (including lactose)
  'eggs', // Eggs and egg products
  'fish', // Fish and fish products
  'crustaceans', // Crabs, lobsters, shrimp, etc.
  'treeNuts', // Almonds, hazelnuts, walnuts, cashews, pecans, etc.
  'peanuts', // Peanuts and peanut products
  'wheat', // Wheat (Note: EU uses broader "gluten" category)
  'soybeans', // Soy and soy products
  'sesame', // Sesame seeds and oil

  // --- EU-ONLY allergens (not in US regulation) ---
  'gluten', // Cereals containing gluten (wheat, rye, barley, oats, spelt)
  'celery', // Celery and celeriac
  'mustard', // Mustard seeds and products
  'sulphites', // Sulphur dioxide and sulphites (> 10mg/kg)
  'lupin', // Lupin beans and flour
  'molluscs', // Mussels, clams, oysters, squid, snails
]);
export type AllergenCode = z.infer<typeof AllergenCode>;

// -----------------------------------------------------------------------------
// Dietary Codes
// -----------------------------------------------------------------------------

/**
 * Dietary tags for menu items.
 * These are NOT legally mandatory but are a strong UX/conversion expectation.
 * Displayed as small icons/badges next to each menu item.
 */
export const DietCode = z.enum([
  'vegan', // No animal products at all
  'vegetarian', // No meat/fish, may include dairy/eggs
  'glutenFree', // No gluten-containing ingredients
  'halal', // Prepared according to Islamic dietary law
  'kosher', // Prepared according to Jewish dietary law
  'dairyFree', // No milk or dairy products
  'nutFree', // No tree nuts or peanuts
  'organic', // Made with certified organic ingredients
  'spicy', // Contains spicy ingredients (informational)
]);
export type DietCode = z.infer<typeof DietCode>;

// -----------------------------------------------------------------------------
// Menu Item Schema
// -----------------------------------------------------------------------------

/**
 * A single dish or drink on the menu.
 *
 * EXAMPLE:
 * {
 *   "id": "margherita",
 *   "name": "Margherita",
 *   "description": "San Marzano tomatoes, fior di latte, fresh basil, extra virgin olive oil",
 *   "price": 14.50,
 *   "currency": "EUR",
 *   "allergens": ["gluten", "milk"],
 *   "diets": ["vegetarian"],
 *   "available": true,
 *   "categoryId": "pizzas"
 * }
 */
export const MenuItemSchema = z.object({
  /** Unique identifier for this item (used internally, not shown to customers) */
  id: z.string().min(1, 'Item ID is required — use a slug like "margherita" or "tiramisu"'),

  /** Display name of the dish/drink */
  name: z.string().min(1, 'Item name is required'),

  /** Optional description — ingredients, preparation method, etc. */
  description: z.string().optional(),

  /** Price as a number (e.g., 14.50, not "14,50€")
   *  The currency symbol is added automatically from the site config */
  price: z.number().nonnegative('Price cannot be negative'),

  /** ISO 4217 currency code — "EUR", "USD", "GBP", "BRL", etc.
   *  Usually the same for all items (set once in site.config.ts) */
  currency: z.string().length(3, 'Use 3-letter ISO currency code like "EUR" or "USD"'),

  /** Path to a photo of the dish (optional but recommended for conversion) */
  image: z.string().optional(),

  /** Allergens present in this dish — picked from the controlled list above.
   *  In the EU, this is LEGALLY REQUIRED for every dish.
   *  The allergen regulation setting in site.config determines which are enforced. */
  allergens: z.array(AllergenCode).default([]),

  /** Dietary tags — vegan, vegetarian, gluten-free, etc. */
  diets: z.array(DietCode).default([]),

  /** Whether this item is currently available.
   *  Set to false for seasonal items that are temporarily off the menu.
   *  Unavailable items can be hidden or shown as "sold out" depending on config. */
  available: z.boolean().default(true),

  /** The ID of the category this item belongs to (e.g., "starters", "mains")
   *  Must match a category ID in the menu's categories array. */
  categoryId: z.string().min(1, 'Category ID is required'),
});
export type MenuItem = z.infer<typeof MenuItemSchema>;

// -----------------------------------------------------------------------------
// Menu Category Schema
// -----------------------------------------------------------------------------

/**
 * A menu category (e.g., "Starters", "Mains", "Desserts", "Wines").
 * Categories are rendered as tabs or anchored navigation sections.
 */
export const MenuCategorySchema = z.object({
  /** Unique identifier for this category (used as anchor ID in the URL) */
  id: z.string().min(1, 'Category ID is required — use a slug like "starters" or "wines"'),

  /** Display name of the category */
  name: z.string().min(1, 'Category name is required'),

  /** Optional description shown under the category heading */
  description: z.string().optional(),

  /** Display order (lower numbers appear first)
   *  If you want Starters → Mains → Desserts → Drinks, use 1 → 2 → 3 → 4 */
  sortOrder: z.number().int().nonnegative().default(0),
});
export type MenuCategory = z.infer<typeof MenuCategorySchema>;

// -----------------------------------------------------------------------------
// Complete Menu Schema
// -----------------------------------------------------------------------------

/**
 * The full menu data structure containing categories and items.
 *
 * USAGE:
 * Create a file: apps/site/content/menu.json
 * {
 *   "categories": [
 *     { "id": "starters", "name": "Starters", "sortOrder": 1 },
 *     { "id": "mains",    "name": "Main Courses", "sortOrder": 2 },
 *     { "id": "desserts", "name": "Desserts", "sortOrder": 3 }
 *   ],
 *   "items": [
 *     { "id": "bruschetta", "name": "Bruschetta", ... "categoryId": "starters" },
 *     { "id": "lasagna",    "name": "Lasagna",    ... "categoryId": "mains" },
 *     ...
 *   ]
 * }
 */
export const MenuSchema = z.object({
  /** All menu categories (displayed as tabs or section headings) */
  categories: z.array(MenuCategorySchema).min(1, 'At least one menu category is required'),

  /** All menu items across all categories */
  items: z.array(MenuItemSchema).min(1, 'At least one menu item is required'),
});
export type Menu = z.infer<typeof MenuSchema>;
